import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_USERWITHPASSWORDFORGET } from "../../../utils/constants";
import { ForgetPasswordInterface } from "../../../domain/helpers/ForgetPassword";
import userService from "../../../services/userService/userService";

interface UpdateUsersContext {
  previousUsers: ForgetPasswordInterface[];
}

const useUpdateUserWithPassForget = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    ForgetPasswordInterface,
    Error,
    { email: string; password: string },
    UpdateUsersContext
  >({
    mutationFn: async ({ email, password }) => {
      // Call the service with only email and password
      return userService.ResetPassword(email, password);
    },

    onMutate: async (updatedUser) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_USERWITHPASSWORDFORGET, page, pageSize],
      });

      const previousUsers =
        queryClient.getQueryData<ForgetPasswordInterface[]>([
          CACHE_KEY_USERWITHPASSWORDFORGET,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<ForgetPasswordInterface[]>(
        [CACHE_KEY_USERWITHPASSWORDFORGET, page, pageSize],
        (users = []) =>
          users.map((user) =>
            user.email === updatedUser.email
              ? { ...user, resetPassword: true }
              : user
          )
      );

      return { previousUsers };
    },

    onSuccess: (savedUser) => {
      queryClient.setQueryData<ForgetPasswordInterface[]>(
        [CACHE_KEY_USERWITHPASSWORDFORGET, page, pageSize],
        (users) =>
          users?.map((user) =>
            user.email === savedUser.email ? savedUser : user
          )
      );
    },

    onError: (error, updatedUser, context) => {
      if (!context) return;

      queryClient.setQueryData<ForgetPasswordInterface[]>(
        [CACHE_KEY_USERWITHPASSWORDFORGET, page, pageSize],
        context.previousUsers
      );
    },
  });
};

export default useUpdateUserWithPassForget;
